import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    LayoutDashboard, ShoppingBag, Truck, FileText, CheckSquare,
    Users, BarChart2, Settings, MessageSquare, LogOut, X, ChevronDown, ChevronRight, Tag, Gift,
    Calendar, UserCog, Radar, PieChart, LifeBuoy, Headphones, ShieldAlert, Archive, Ban, Star, DatabaseBackup, ShoppingCart
} from 'lucide-react';
import SidebarItem from '../../../ui-kit/SidebarItem';
import { cn } from '../../../shared/utils';
import { useChat } from '../../chat/state/ChatContext';
import { can, canUser } from '../../../core/permissions';
import { moduleAllowed } from '../../../core/permissions/modules';
import { getUsageReport, onUsageChange, formatBytes } from '../../../integrations/rtdbMeter';
import { listApprovals } from '../../approvals/approvalsApi';
import { ShieldCheck } from 'lucide-react';

/**
 * Icons-only menu (owner, 30 Oct 2026): a group (Orders, Ship Order) is one icon; hovering
 * it opens its list beside the rail. Rendered into <body> so the rail's scroll box and
 * its transform cannot clip it.
 */
const RailGroup = ({ icon: Icon, label, active, items }) => {
    const [pos, setPos] = useState(null);
    const t = useRef(null);
    const open = (e) => { clearTimeout(t.current); const r = e.currentTarget.getBoundingClientRect(); setPos({ top: r.top, left: r.right + 6 }); };
    const close = () => { clearTimeout(t.current); t.current = setTimeout(() => setPos(null), 180); };
    return (
        <div onMouseEnter={open} onMouseLeave={close}>
            <button type="button" title={label} aria-label={label} onClick={open}
                className={cn("w-full flex items-center justify-center py-3 rounded-lg transition-colors mb-1",
                    active ? "bg-slate-800 text-white" : "text-slate-400 hover:text-white hover:bg-slate-800")}>
                <Icon size={20} />
            </button>
            {pos && createPortal(
                <div onMouseEnter={() => clearTimeout(t.current)} onMouseLeave={close} role="menu"
                    style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 80 }}
                    className="w-56 bg-slate-900 text-white rounded-xl shadow-2xl border border-slate-700 p-2">
                    <div className="px-2 pb-1 text-[11px] uppercase tracking-wider text-slate-400 font-bold">{label}</div>
                    {items.map(it => (
                        <button key={it.key} type="button" role="menuitem" onClick={() => { setPos(null); it.onClick(); }}
                            className={cn("w-full text-left text-sm py-1.5 px-2 rounded hover:bg-slate-800 transition-colors flex items-center gap-1.5",
                                it.active ? "text-purple-300 font-bold" : "text-slate-300")}>
                            {it.icon && <it.icon size={13} />} {it.label}
                        </button>
                    ))}
                </div>,
                document.body,
            )}
        </div>
    );
};

const Sidebar = ({ activeTab, onTabChange, onLogout, onClose, collapsed = false }) => {
    const [isOrdersOpen, setIsOrdersOpen] = useState(true);
    const [isShipOrderOpen, setIsShipOrderOpen] = useState(true);
    const { currentUser } = useChat();

    // Role-based capability check (fail-closed for unknown roles/capabilities).
    const hasPermission = (permKey) => canUser(currentUser, permKey);
    const am = currentUser?.allowedModules;
    // The role and the save time matter: a super admin is never narrowed, and a module
    // added after this checklist was saved was never one an admin declined.
    const amCtx = { savedAt: currentUser?.allowedModulesSavedAt, role: currentUser?.role };
    const show = (cap, key) => hasPermission(cap) && moduleAllowed(am, key, amCtx);

    // Live cost meter per module. Realtime Database is billed per GB downloaded, and
    // the bill never says WHICH screen spent it — so each item carries what it has
    // pulled in this browser. A module showing tens of megabytes is reading far more
    // than it renders, and that is the one to look at.
    const [usage, setUsage] = useState({ total: 0, byModule: {} });
    useEffect(() => {
        const read = () => {
            const rep = getUsageReport();
            const byModule = {};
            rep.modules.forEach(m => { byModule[m.module] = m.bytes; });
            setUsage({ total: rep.total, byModule });
        };
        read();
        return onUsageChange(read);
    }, []);
    // Bytes for a sidebar key. The meter keys off the first path segment, so hyphenated
    // routes ('india-post', 'quick-order') match as they are; only the orders sub-tabs
    // share a parent route and have to be folded in by hand.
    const meterFor = (key) => usage.byModule[/^orders-/.test(key) ? 'orders' : key] || 0;

    // Approval Desk (managers only) with a live count of what is waiting — one small call a
    // minute, answered from the proxy's memory.
    const isManager = show('canViewApprovals', 'approvals');
    const [approvalCount, setApprovalCount] = useState(0);
    useEffect(() => {
        if (!isManager) return undefined;
        let live = true;
        const tick = () => listApprovals().then(r => { if (live) setApprovalCount(r.count || 0); }).catch(() => {});
        tick();
        const t = setInterval(tick, 60000);
        return () => { live = false; clearInterval(t); };
    }, [isManager]);
    const ApprovalIcon = (p) => (
        <span className="relative inline-flex">
            <ShieldCheck {...p} />
            {approvalCount > 0 && <span className="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[10px] font-black leading-4 text-center">{approvalCount}</span>}
        </span>
    );

    // The two groups' entries, shared by the icons-only rail's pop-outs.
    const tab = (key, label, icon) => ({ key, label, icon, active: activeTab === key, onClick: () => onTabChange(key) });
    const ordersItems = [tab('orders', 'All Orders'), tab('orders-pending', 'Pending'), tab('orders-shipped', 'Shipped'), tab('orders-delivered', 'Delivered')];
    const shipItems = [
        tab('shiprocket', 'Shiprocket'), tab('dtdc', 'DTDC'), tab('india-post', 'India Post'), tab('ekart', 'Ekart'),
        ...(show('canViewHandover', 'handover') ? [{ key: 'handover', label: 'Handover Scan', onClick: () => { window.location.href = '/handover'; } }] : []),
        ...(show('canViewQc', 'qc') ? [{ key: 'qc', label: 'Parcel QC', onClick: () => { window.location.href = '/qc'; } }] : []),
        tab('archived', 'Archived Orders', Archive), tab('cancelled', 'Cancelled Orders', Ban), tab('abandoned', 'Abandoned (Website)', ShoppingCart),
    ];
    const shipActive = ['shiprocket', 'india-post', 'dtdc', 'ekart', 'archived', 'cancelled', 'abandoned'].includes(activeTab);

    return (
        <div className="w-full bg-slate-900 text-white h-full flex flex-col transition-all duration-300">
            <div className={cn("border-b border-slate-800 flex items-center", collapsed ? "p-4 justify-center" : "p-6 justify-between")}>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-purple-900/50">
                        <span className="text-xl font-bold text-white">D</span>
                    </div>
                    {!collapsed && (
                        <div>
                            <h1 className="text-xl font-bold tracking-tight">Daksha</h1>
                            <p className="text-xs text-slate-400 uppercase tracking-widest">Organics</p>
                        </div>
                    )}
                </div>
                {/* Mobile Close Button */}
                <button
                    onClick={onClose}
                    className="md:hidden p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
                >
                    <X size={24} />
                </button>
            </div>

            {collapsed ? (
                <nav className="flex-1 overflow-y-auto overflow-x-hidden py-4 px-2 space-y-1" aria-label="Menu (icons only)">
                    {show('canViewDashboard', 'dashboard') && <SidebarItem collapsed icon={LayoutDashboard} label="Dashboard" active={activeTab === 'dashboard'} onClick={() => onTabChange('dashboard')} />}
                    {show('canViewParser', 'parser') && <SidebarItem collapsed icon={FileText} label="Parser" active={activeTab === 'parser'} onClick={() => onTabChange('parser')} />}
                    {show('canViewOrders', 'orders') && <RailGroup icon={ShoppingBag} label="Orders" active={activeTab.startsWith('orders')} items={ordersItems} />}
                    {show('canViewFinance', 'invoices') && <SidebarItem collapsed icon={FileText} label="Invoices" active={activeTab === 'invoices'} onClick={() => onTabChange('invoices')} />}
                    {show('canViewOrders', 'labels') && <SidebarItem collapsed icon={Tag} label="Labels" active={activeTab === 'labels'} onClick={() => onTabChange('labels')} />}
                    {show('canViewTracking', 'tracking') && <RailGroup icon={Truck} label="Ship Order" active={shipActive} items={shipItems} />}
                    {show('canViewTracking', 'tracking') && <SidebarItem collapsed icon={Radar} label="Tracking" active={activeTab === 'tracking'} onClick={() => onTabChange('tracking')} />}
                    {show('canViewAnalytics', 'insights') && <SidebarItem collapsed icon={PieChart} label="Customer Insights" active={activeTab === 'insights'} onClick={() => onTabChange('insights')} />}
                    <div className="my-3 border-t border-slate-800 mx-1"></div>
                    {show('canViewProducts', 'products') && <SidebarItem collapsed icon={ShoppingBag} label="Products" active={activeTab === 'products'} onClick={() => onTabChange('products')} />}
                    {show('canViewCustomers', 'customers') && <SidebarItem collapsed icon={Users} label="Customers" active={activeTab === 'customers'} onClick={() => onTabChange('customers')} />}
                    {show('canViewRewards', 'rewards') && <SidebarItem collapsed icon={Star} label="Rewards & Wallet" active={activeTab === 'rewards'} onClick={() => onTabChange('rewards')} />}
                    {show('canViewTelecallerWorkspace', 'telecaller') && <SidebarItem collapsed icon={Headphones} label="Telecaller Workspace" active={activeTab === 'telecaller'} onClick={() => onTabChange('telecaller')} />}
                    {show('canViewComplaints', 'complaints') && <SidebarItem collapsed icon={LifeBuoy} label="Support / Complaints" active={activeTab === 'complaints'} onClick={() => onTabChange('complaints')} />}
                    {show('canViewComplaints', 'quick-order') && <SidebarItem collapsed icon={ShoppingCart} label="Quick Order (Online)" active={activeTab === 'quick-order'} onClick={() => onTabChange('quick-order')} />}
                    {show('canViewCourierEscalations', 'escalations') && <SidebarItem collapsed icon={ShieldAlert} label="Courier Escalations" active={activeTab === 'escalations'} onClick={() => onTabChange('escalations')} />}
                    {isManager && <SidebarItem collapsed icon={ApprovalIcon} label={`Approval Desk${approvalCount ? ` (${approvalCount})` : ''}`} active={activeTab === 'approvals'} onClick={() => onTabChange('approvals')} />}
                    {show('canViewTodo', 'todo') && <SidebarItem collapsed icon={CheckSquare} label="To-Do" active={activeTab === 'todo'} onClick={() => onTabChange('todo')} />}
                    {show('canViewCalendar', 'calendar') && <SidebarItem collapsed icon={Calendar} label="Calendar" active={activeTab === 'calendar'} onClick={() => onTabChange('calendar')} />}
                    {show('canViewStaff', 'staff') && <SidebarItem collapsed icon={UserCog} label="Staff" active={activeTab === 'staff'} onClick={() => onTabChange('staff')} />}
                    {show('canViewChat', 'chat') && <SidebarItem collapsed icon={MessageSquare} label="Chat" active={activeTab === 'chat'} onClick={() => onTabChange('chat')} />}
                    {show('canViewAnalytics', 'analytics') && <SidebarItem collapsed icon={BarChart2} label="Analytics" active={activeTab === 'analytics'} onClick={() => onTabChange('analytics')} />}
                    {show('canViewReferrals', 'referrals') && <SidebarItem collapsed icon={Gift} label="Referrals" active={activeTab === 'referrals'} onClick={() => onTabChange('referrals')} />}
                    {show('canManageSettings', 'backup') && <SidebarItem collapsed icon={DatabaseBackup} label="Backup & Restore" active={activeTab === 'backup'} onClick={() => onTabChange('backup')} />}
                    {show('canManageSettings', 'settings') && <SidebarItem collapsed icon={Settings} label="Settings" active={activeTab === 'settings'} onClick={() => onTabChange('settings')} />}
                </nav>
            ) : (
            <nav className="flex-1 overflow-y-auto py-6 px-3 space-y-1">
                {show('canViewDashboard', 'dashboard') && <SidebarItem icon={LayoutDashboard} label="Dashboard" active={activeTab === 'dashboard'} onClick={() => onTabChange('dashboard')} meter={meterFor('dashboard')} />}

                {/* Orders Section */}
                {show('canViewParser', 'parser') && <SidebarItem icon={FileText} label="Parser" active={activeTab === 'parser'} onClick={() => onTabChange('parser')} meter={meterFor('parser')} />}
                {show('canViewOrders', 'orders') && (
                    <div>
                        <button
                            onClick={() => setIsOrdersOpen(!isOrdersOpen)}
                            className={cn(
                                "w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors mb-1",
                                activeTab.startsWith('orders') ? "text-white bg-slate-800" : "text-slate-400 hover:text-white hover:bg-slate-800"
                            )}
                        >
                            <div className="flex items-center gap-3">
                                <ShoppingBag size={20} />
                                <span className="font-medium">Orders</span>
                            </div>
                            {isOrdersOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        </button>

                        {isOrdersOpen && (
                            <div className="pl-10 space-y-1 mb-2">
                                <button
                                    onClick={() => onTabChange('orders')}
                                    className={cn("w-full text-left text-sm py-1.5 px-2 rounded hover:text-white transition-colors", activeTab === 'orders' ? "text-purple-400 font-bold" : "text-slate-500")}
                                >
                                    All Orders
                                </button>
                                <button
                                    onClick={() => onTabChange('orders-pending')}
                                    className={cn("w-full text-left text-sm py-1.5 px-2 rounded hover:text-white transition-colors", activeTab === 'orders-pending' ? "text-purple-400 font-bold" : "text-slate-500")}
                                >
                                    Pending
                                </button>
                                <button
                                    onClick={() => onTabChange('orders-shipped')}
                                    className={cn("w-full text-left text-sm py-1.5 px-2 rounded hover:text-white transition-colors", activeTab === 'orders-shipped' ? "text-purple-400 font-bold" : "text-slate-500")}
                                >
                                    Shipped
                                </button>
                                <button
                                    onClick={() => onTabChange('orders-delivered')}
                                    className={cn("w-full text-left text-sm py-1.5 px-2 rounded hover:text-white transition-colors", activeTab === 'orders-delivered' ? "text-purple-400 font-bold" : "text-slate-500")}
                                >
                                    Delivered
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {show('canViewFinance', 'invoices') && <SidebarItem icon={FileText} label="Invoices" active={activeTab === 'invoices'} onClick={() => onTabChange('invoices')} meter={meterFor('invoices')} />}
                {show('canViewOrders', 'labels') && <SidebarItem icon={Tag} label="Labels" active={activeTab === 'labels'} onClick={() => onTabChange('labels')} meter={meterFor('labels')} />}
                {/* Ship Order Section */}
                {show('canViewTracking', 'tracking') && (
                    <div>
                        <button
                            onClick={() => setIsShipOrderOpen(!isShipOrderOpen)}
                            className={cn(
                                "w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors mb-1",
                                (activeTab === 'shiprocket' || activeTab === 'india-post' || activeTab === 'dtdc' || activeTab === 'ekart') ? "text-white bg-slate-800" : "text-slate-400 hover:text-white hover:bg-slate-800"
                            )}
                        >
                            <div className="flex items-center gap-3">
                                <Truck size={20} />
                                <span className="font-medium">Ship Order</span>
                            </div>
                            {isShipOrderOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        </button>

                        {isShipOrderOpen && (
                            <div className="pl-10 space-y-1 mb-2">
                                <button
                                    onClick={() => onTabChange('shiprocket')}
                                    className={cn("w-full text-left text-sm py-1.5 px-2 rounded hover:text-white transition-colors", activeTab === 'shiprocket' ? "text-purple-400 font-bold" : "text-slate-500")}
                                >
                                    Shiprocket
                                </button>
                                <button
                                    onClick={() => onTabChange('dtdc')}
                                    className={cn("w-full text-left text-sm py-1.5 px-2 rounded hover:text-white transition-colors", activeTab === 'dtdc' ? "text-purple-400 font-bold" : "text-slate-500")}
                                >
                                    DTDC
                                </button>
                                <button
                                    onClick={() => onTabChange('india-post')}
                                    className={cn("w-full text-left text-sm py-1.5 px-2 rounded hover:text-white transition-colors", activeTab === 'india-post' ? "text-purple-400 font-bold" : "text-slate-500")}
                                >
                                    India Post
                                </button>
                                <button
                                    onClick={() => onTabChange('ekart')}
                                    className={cn("w-full text-left text-sm py-1.5 px-2 rounded hover:text-white transition-colors", activeTab === 'ekart' ? "text-purple-400 font-bold" : "text-slate-500")}
                                >
                                    Ekart
                                </button>
                                {show('canViewHandover', 'handover') && (
                                    <button
                                        onClick={() => { window.location.href = '/handover'; }}
                                        className="w-full text-left text-sm py-1.5 px-2 rounded text-slate-500 hover:text-white transition-colors"
                                    >
                                        Handover Scan
                                    </button>
                                )}
                                {show('canViewQc', 'qc') && (
                                    <button
                                        onClick={() => { window.location.href = '/qc'; }}
                                        className="w-full text-left text-sm py-1.5 px-2 rounded text-slate-500 hover:text-white transition-colors"
                                    >
                                        Parcel QC
                                    </button>
                                )}
                                <button
                                    onClick={() => onTabChange('archived')}
                                    className={cn("w-full text-left text-sm py-1.5 px-2 rounded hover:text-white transition-colors flex items-center gap-1.5", activeTab === 'archived' ? "text-purple-400 font-bold" : "text-slate-500")}
                                >
                                    <Archive size={13} /> Archived Orders
                                </button>
                                <button
                                    onClick={() => onTabChange('cancelled')}
                                    className={cn("w-full text-left text-sm py-1.5 px-2 rounded hover:text-white transition-colors flex items-center gap-1.5", activeTab === 'cancelled' ? "text-purple-400 font-bold" : "text-slate-500")}
                                >
                                    <Ban size={13} /> Cancelled Orders
                                </button>
                                <button
                                    onClick={() => onTabChange('abandoned')}
                                    className={cn("w-full text-left text-sm py-1.5 px-2 rounded hover:text-white transition-colors flex items-center gap-1.5", activeTab === 'abandoned' ? "text-purple-400 font-bold" : "text-slate-500")}
                                >
                                    <ShoppingCart size={13} /> Abandoned (Website)
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* Tracking Section */}
                {show('canViewTracking', 'tracking') && (
                    <SidebarItem
                        icon={Radar}
                        label="Tracking"
                        active={activeTab === 'tracking'}
                        onClick={() => onTabChange('tracking')} meter={meterFor('tracking')}
                    />
                )}

                {/* Customer Insights */}
                {show('canViewAnalytics', 'insights') && <SidebarItem icon={PieChart} label="Customer Insights" active={activeTab === 'insights'} onClick={() => onTabChange('insights')} meter={meterFor('insights')} />}

                <div className="my-4 border-t border-slate-800 mx-2"></div>

                {show('canViewProducts', 'products') && <SidebarItem icon={ShoppingBag} label="Products" active={activeTab === 'products'} onClick={() => onTabChange('products')} meter={meterFor('products')} />}

                {show('canViewCustomers', 'customers') && <SidebarItem icon={Users} label="Customers" active={activeTab === 'customers'} onClick={() => onTabChange('customers')} meter={meterFor('customers')} />}

                {show('canViewRewards', 'rewards') && <SidebarItem icon={Star} label="Rewards & Wallet" active={activeTab === 'rewards'} onClick={() => onTabChange('rewards')} meter={meterFor('rewards')} />}

                {show('canViewTelecallerWorkspace', 'telecaller') && <SidebarItem icon={Headphones} label="Telecaller Workspace" active={activeTab === 'telecaller'} onClick={() => onTabChange('telecaller')} meter={meterFor('telecaller')} />}

                {show('canViewComplaints', 'complaints') && <SidebarItem icon={LifeBuoy} label="Support / Complaints" active={activeTab === 'complaints'} onClick={() => onTabChange('complaints')} meter={meterFor('complaints')} />}

                {show('canViewComplaints', 'quick-order') && <SidebarItem icon={(p) => <span {...p} className={`inline-flex items-center justify-center ${p.className || ''}`} style={{ fontSize: 16, lineHeight: 1 }}>🛒</span>} label="Quick Order (Online)" active={activeTab === 'quick-order'} onClick={() => onTabChange('quick-order')} meter={meterFor('quick-order')} />}

                {show('canViewCourierEscalations', 'escalations') && <SidebarItem icon={ShieldAlert} label="Courier Escalations" active={activeTab === 'escalations'} onClick={() => onTabChange('escalations')} meter={meterFor('escalations')} />}

                {isManager && <SidebarItem icon={ApprovalIcon} label={approvalCount ? `Approval Desk (${approvalCount})` : 'Approval Desk'} active={activeTab === 'approvals'} onClick={() => onTabChange('approvals')} />}

                {show('canViewTodo', 'todo') && <SidebarItem icon={CheckSquare} label="To-Do" active={activeTab === 'todo'} onClick={() => onTabChange('todo')} meter={meterFor('todo')} />}

                {show('canViewCalendar', 'calendar') && <SidebarItem icon={Calendar} label="Calendar" active={activeTab === 'calendar'} onClick={() => onTabChange('calendar')} meter={meterFor('calendar')} />}

                {show('canViewStaff', 'staff') && <SidebarItem icon={UserCog} label="Staff" active={activeTab === 'staff'} onClick={() => onTabChange('staff')} meter={meterFor('staff')} />}

                {show('canViewChat', 'chat') && <SidebarItem icon={MessageSquare} label="Chat" active={activeTab === 'chat'} onClick={() => onTabChange('chat')} meter={meterFor('chat')} />}

                {show('canViewAnalytics', 'analytics') && <SidebarItem icon={BarChart2} label="Analytics" active={activeTab === 'analytics'} onClick={() => onTabChange('analytics')} meter={meterFor('analytics')} />}

                {show('canViewReferrals', 'referrals') && <SidebarItem icon={Gift} label="Referrals" active={activeTab === 'referrals'} onClick={() => onTabChange('referrals')} meter={meterFor('referrals')} />}

                {show('canManageSettings', 'backup') && <SidebarItem icon={DatabaseBackup} label="Backup & Restore" active={activeTab === 'backup'} onClick={() => onTabChange('backup')} meter={meterFor('backup')} />}
                {show('canManageSettings', 'settings') && <SidebarItem icon={Settings} label="Settings" active={activeTab === 'settings'} onClick={() => onTabChange('settings')} meter={meterFor('settings')} />}
            </nav>
            )}

            {collapsed ? (
                <div className="p-2 border-t border-slate-800 space-y-1">
                    {usage.total > 0 && (
                        <button onClick={() => onTabChange('data-usage')} title={`Data used in this browser: ${formatBytes(usage.total)}`}
                            className={cn('w-full py-2 rounded-lg text-[10px] font-bold tabular-nums hover:bg-slate-800',
                                usage.total >= 200 * 1024 * 1024 ? 'text-red-400' : usage.total >= 50 * 1024 * 1024 ? 'text-amber-300' : 'text-slate-300')}>
                            {formatBytes(usage.total)}
                        </button>
                    )}
                    <button onClick={onLogout} title="Logout" aria-label="Logout" className="w-full flex justify-center py-3 rounded-lg text-red-400 hover:bg-red-900/20">
                        <LogOut size={20} />
                    </button>
                </div>
            ) : (
            <div className="p-4 border-t border-slate-800 space-y-2">
                {/* What this browser has pulled in total. Clicking opens the full
                    breakdown, where the exact paths behind a heavy module are listed. */}
                {usage.total > 0 && (
                    <button
                        onClick={() => onTabChange('data-usage')}
                        title="Database data downloaded in this browser. Realtime Database is billed per GB — open for the full breakdown."
                        className="w-full flex items-center justify-between gap-2 px-4 py-2 rounded-lg text-[11px] text-slate-400 hover:bg-slate-800 hover:text-slate-200 transition-colors"
                    >
                        <span className="flex items-center gap-2"><Radar size={13} /> Data used</span>
                        <span className={cn('font-bold tabular-nums',
                            usage.total >= 200 * 1024 * 1024 ? 'text-red-400'
                                : usage.total >= 50 * 1024 * 1024 ? 'text-amber-300' : 'text-slate-300')}>
                            {formatBytes(usage.total)}
                        </span>
                    </button>
                )}
                <button onClick={onLogout} className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-red-400 hover:bg-red-900/20 transition-all duration-200">
                    <LogOut size={20} />
                    <span className="font-medium">Logout</span>
                </button>
            </div>
            )}
        </div>
    );
};

export default Sidebar;
